import _extends from '@babel/runtime/helpers/extends';
import React, { Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes } from './spacing-0f39ba4a.js';
import { G as Group } from './Group-0add3181.js';
import { A as Avatar } from './Avatar-548d36a9.js';
import { B as Box } from './Box-a46c799c.js';
import { T as Typography } from './Typography-98688343.js';
import { B as Button } from './Button-e85764bc.js';
import IconMail from './IconMail.js';
import { g as getZIndex, z as zIndexProps } from './zIndex-60238fbe.js';

function AvatarGroupItem({
  children,
  className,
  avatar,
  initials,
  size,
  email,
  displayName,
  margin,
  ...otherProps
}) {
  let cssClasses = ['st_agroup__item', `st_agroup__item--${size}`];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    role: "presentation"
  }), /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    size: size,
    initials: initials
  }));
}
AvatarGroupItem.propTypes = {
  /** array of available avatar images, or url to single image */
  avatar: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    width: PropTypes.number,
    url: PropTypes.string
  })), PropTypes.string]),
  /** fallback letter */
  initials: PropTypes.string,
  /** email address of user */
  email: PropTypes.string,
  /** full name of user */
  displayName: PropTypes.string,
  /** Size of avatars */
  size: PropTypes.oneOf(['lg', 'xl']),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** left/top/right/bottom, and also size */
  margin: marginPropTypes
};
AvatarGroupItem.defaultProps = {
  avatar: null,
  initials: null,
  size: 'lg',
  displayName: null,
  email: null,
  className: null,
  margin: null
};
AvatarGroupItem.displayName = 'AvatarGroup.Item';

function AvatarGroup({
  children,
  className,
  size,
  componentEl: Component,
  margin,
  align,
  zIndex,
  ...otherProps
}) {
  let cssClasses = ['st_agroup'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  if (zIndex) cssClasses = addClassNames(getZIndex(zIndex), cssClasses);

  // make an array of children
  const arrayChildren = Children.toArray(children).filter(c => c.type.displayName === 'AvatarGroup.Item');

  // do not render if there is only one person
  if (arrayChildren.length <= 1) return null;
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    "aria-label": `${arrayChildren.length} users on this page`
  }), /*#__PURE__*/React.createElement(Group, {
    gap: "none",
    direction: "row-reverse",
    className: "st_agroup__trigger",
    inline: true,
    tabIndex: "0"
  }, arrayChildren.map((c, i) => {
    if (arrayChildren.length <= 5 || i < 4 && arrayChildren.length > 5) {
      return /*#__PURE__*/cloneElement(c, {
        size
      });
    }
    return null;
  }), arrayChildren.length > 5 && /*#__PURE__*/React.createElement(Group, {
    role: "presentation",
    align: "center",
    className: `st_agroup__ph st_agroup__ph--${size}`
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "note"
  }, "+5")), /*#__PURE__*/React.createElement("div", {
    className: `st_agroup__dropdown st_agroup__dropdown--${align}`
  }, /*#__PURE__*/React.createElement(Box, {
    padding: {
      all: 'md'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "label1",
    truncate: true
  }, "Other Users On This Page"), arrayChildren.map(c => /*#__PURE__*/React.createElement(Group, {
    gap: "sm",
    wrap: false,
    key: `${c.props.avatar}-${c.props.displayName}-${c.props.initials}`
  }, /*#__PURE__*/React.createElement(Group, {
    gap: "xs",
    wrap: false
  }, /*#__PURE__*/React.createElement(Avatar, {
    role: "presentation",
    initials: c.props.initials,
    avatar: c.props.avatar,
    size: "md"
  }), /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    inline: false,
    truncate: true
  }, c.props.displayName)), c.props.email && /*#__PURE__*/React.createElement(Button, {
    icon: IconMail,
    tertiary: true,
    "aria-label": `email ${c.props.displayName || c.props.initials || 'user'}`,
    componentEl: "a",
    href: `mailto:${c.props.email}`
  })))))));
}
AvatarGroup.propTypes = {
  /** Size of avatars */
  size: PropTypes.oneOf(['lg', 'xl']),
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** which way to align the dropdown */
  align: PropTypes.oneOf(['left', 'right']),
  /**
   * z-index for button container.
   * Ignored for single buttons. One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** left/top/right/bottom, and also size */
  margin: marginPropTypes
};
AvatarGroup.defaultProps = {
  size: 'lg',
  componentEl: 'div',
  className: null,
  margin: null,
  align: 'right',
  zIndex: 'minor'
};
AvatarGroup.Item = AvatarGroupItem;

export { AvatarGroup as A };
